import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WaterIcon from "@mui/icons-material/Water";
import ExploreIcon from "@mui/icons-material/Explore";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PRODUCTS } from "../data/products";

// Icons live here, keyed to each product, rather than in the shared data
// file — keeps src/data/products.js importable from anywhere without
// pulling in MUI icon components.
const ICONS = {
  amyloidconnect: <FavoriteIcon sx={{ fontSize: 80 }} />,
  nephrotrax: <WaterIcon sx={{ fontSize: 80 }} />,
  compass: <ExploreIcon sx={{ fontSize: 80 }} />,
  resourceclan: <VolunteerActivismIcon sx={{ fontSize: 80 }} />,
};

export default function ProductsPage() {
  const navigate = useNavigate();

  const handleCta = (product) => {
    if (product.status !== "Live") {
      navigate(product.path);
      return;
    }
    // externalUrl may be a full external link (AmyloidConnect, NephroTrax)
    // or an internal path (Compass, at /compass) — handled accordingly.
    // Previously this always opened amyloidconnect.com regardless of
    // which product's button was actually clicked.
    if (product.externalUrl?.startsWith("http")) {
      window.open(product.externalUrl, "_blank");
    } else if (product.externalUrl) {
      navigate(product.externalUrl);
    } else {
      navigate(product.path);
    }
  };

  return (
    <Box>
      {/* HERO */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a237e 0%, #00897b 100%)",
          color: "white",
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Chip
            label="Our Products"
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              color: "white",
              fontWeight: 600,
              mb: 3,
            }}
          />
          <Typography variant="h2" fontWeight={800} gutterBottom>
            Disease-Specific Platforms
          </Typography>
          <Typography variant="h5" sx={{ opacity: 0.9, fontWeight: 400 }}>
            Purpose-built platforms for specific patient communities — not
            generic health tools.
          </Typography>
        </Container>
      </Box>

      {/* PRODUCTS */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "#f8fafc" }}>
        <Container maxWidth="lg">
          <Grid container spacing={6}>
            {PRODUCTS.map((product) => (
              <Grid item xs={12} key={product.key}>
                <Card
                  sx={{
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": { boxShadow: 8 },
                  }}
                >
                  <Grid container>
                    <Grid item xs={12} md={4}>
                      <Box
                        sx={{
                          background: product.gradient,
                          height: "100%",
                          minHeight: 300,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          p: 4,
                          textAlign: "center",
                        }}
                      >
                        <Box sx={{ color: "rgba(255,255,255,0.9)", mb: 2 }}>
                          {ICONS[product.key]}
                        </Box>
                        <Typography variant="h4" fontWeight={800}>
                          {product.name}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{ opacity: 0.9, mt: 1 }}
                        >
                          {product.tagline}
                        </Typography>
                        <Chip
                          label={product.status}
                          color={product.statusColor}
                          size="small"
                          sx={{ mt: 2, fontWeight: 700 }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <CardContent sx={{ p: 4 }}>
                        <Typography
                          variant="body1"
                          color="text.secondary"
                          paragraph
                          sx={{ lineHeight: 1.8, fontSize: "1.1rem" }}
                        >
                          {product.description}
                        </Typography>
                        <Grid container spacing={1.5} sx={{ mb: 3 }}>
                          {product.features.map((feature, idx) => (
                            <Grid item xs={12} sm={6} key={idx}>
                              <Card
                                variant="outlined"
                                sx={{
                                  borderColor: `${product.color}30`,
                                  bgcolor: `${product.color}08`,
                                  height: "100%",
                                }}
                              >
                                <CardContent
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    p: "10px 14px !important",
                                  }}
                                >
                                  <CheckCircleIcon
                                    sx={{ color: product.color, fontSize: 18, flexShrink: 0 }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: "text.primary" }}
                                  >
                                    {feature}
                                  </Typography>
                                </CardContent>
                              </Card>
                            </Grid>
                          ))}
                        </Grid>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 3 }}
                        >
                          🌐 {product.url}
                        </Typography>
                        <Button
                          variant="contained"
                          size="large"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => handleCta(product)}
                          sx={{
                            background: product.gradient,
                            fontWeight: 700,
                            px: 4,
                            "&:hover": { opacity: 0.9 },
                          }}
                        >
                          {product.cta}
                        </Button>
                      </CardContent>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}